import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, itDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, DownloadSimple } from "@phosphor-icons/react";

const n2 = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export default function Piecework() {
  const [operators, setOperators] = useState([]);
  const [employeeId, setEmployeeId] = useState("all");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/weights/operators").then((r) => setOperators(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const params = { date_from: from, date_to: to };
      if (employeeId !== "all") params.employee_id = employeeId;
      setData((await api.get("/weights/piecework-summary", { params })).data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [employeeId, from, to]);
  useEffect(() => { load(); }, [load]);

  const qs = `date_from=${from}&date_to=${to}${employeeId !== "all" ? `&employee_id=${employeeId}` : ""}`;
  const name = data?.name || "cottimisti";
  const get = (fmt, ext) => download(`/weights/export/cottimista-periodo?fmt=${fmt}&${qs}`,
    `${name.replace(/ /g, "_")}_${from}_${to}.${ext}`).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="space-y-3" data-testid="piecework-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Cottimisti</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Riepilogo personale da data a data: kg assegnati, dettaglio lavorazioni e stampa.
          </p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2 items-center">
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="h-9 w-[230px] rounded-sm" data-testid="pw-employee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutti i cottimisti</SelectItem>
              {operators.map((o) => <SelectItem key={o.employee_id} value={o.employee_id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-[150px] rounded-sm" data-testid="pw-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="h-9 w-[150px] rounded-sm" data-testid="pw-to" />
          <Button size="sm" className="rounded-sm font-semibold" data-testid="pw-pdf" onClick={() => get("pdf", "pdf")}>
            <Printer size={14} className="mr-1.5" /> STAMPA PDF
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm" data-testid="pw-cards"
            onClick={() => download(`/weights/export/cottimisti-schede?date_from=${from}&date_to=${to}`,
              `schede_cottimisti_${from}_${to}.pdf`).catch((e) => toast.error(errMsg(e)))}>
            <Printer size={14} className="mr-1.5" /> SCHEDE DI TUTTI
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm" data-testid="pw-xlsx" onClick={() => get("xlsx", "xlsx")}>
            <DownloadSimple size={14} className="mr-1.5" /> EXCEL
          </Button>
        </div>
      </div>

      {data && employeeId === "all" && data.operators && (
        <div className="grid lg:grid-cols-3 gap-3">
          <div className="rounded-sm border border-border bg-card overflow-x-auto lg:col-span-2">
            <table className="w-full text-sm tbl-sticky" data-testid="pw-all-table">
              <thead className="bg-secondary"><tr>
                {["Cottimista", "Kg assegnati"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(data.operators || []).map((o) => (
                  <tr key={o.employee_id} className="border-t border-border hover:bg-secondary/40"
                    data-testid={`pw-op-${o.employee_id}`}>
                    <td className="px-3 py-1.5 font-semibold">{o.name}</td>
                    <td className="px-3 py-1.5 tabular-nums font-semibold">{n2(o.weight)}</td>
                  </tr>
                ))}
                {(data.operators || []).length === 0 && (
                  <tr><td colSpan={2} className="px-3 py-8 text-center text-muted-foreground" data-testid="pw-empty">
                    Nessuna divisione registrata nel periodo
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="rounded-sm border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="pw-lines-table">
              <thead className="bg-secondary"><tr>
                {["Totale generale per linea", "Kg"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(data.lines || []).map((l) => (
                  <tr key={l.line} className="border-t border-border" data-testid={`pw-line-${l.line}`}>
                    <td className="px-3 py-1.5 font-semibold">{l.line}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n2(l.weight)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-border bg-secondary/50">
                  <td className="px-3 py-1.5 font-bold">TOTALE</td>
                  <td className="px-3 py-1.5 tabular-nums font-bold" data-testid="pw-lines-total">{n2(data.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && employeeId !== "all" && data.rows && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["Kg assegnati", n2(data.total), "pw-total"],
              ["Giornate", data.days.length, "pw-days"], ["Cottimista", data.name || "—", "pw-name"]].map(([l, v, t]) => (
                <div key={t} className="rounded-sm border border-border bg-card p-3" data-testid={t}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="mt-1 text-xl font-head font-bold tabular-nums truncate">{v}</div>
                </div>
              ))}
          </div>
          <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
            <table className="w-full text-sm tbl-sticky" data-testid="pw-detail-table">
              <thead className="bg-secondary"><tr>
                {["Data", "Sigla", "Lavorazione", "Cliente", "Linea", "Peso totale", "% sua", "Kg assegnati", "Squadra"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`pw-row-${r.id}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap">{itDate(r.date)}</td>
                    <td className="px-3 py-1.5 font-semibold">{r.code}</td>
                    <td className="px-3 py-1.5">{r.description}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.client}</td>
                    <td className="px-3 py-1.5">{r.line}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n2(r.weight)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{r.percent}%</td>
                    <td className="px-3 py-1.5 tabular-nums font-semibold">{n2(r.quota)}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.squadra}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground" data-testid="pw-detail-empty">
                    Nessuna lavorazione assegnata nel periodo
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
